import type { ReactElement } from 'react';

import { useTheme } from '@emotion/react';
import { isNil } from 'ramda';


import { FormattedCurrencyNumber } from '../format';
import type { TypographyProps } from '../typography';
import { Typography } from '../typography';

import * as styles from './amount.styles';

type Size = 'big' | 'medium' | 'small' | 'compact';

type Props = {
  amount: string | number;
  currency?: string;
  currencyDisplay?: Intl.NumberFormatOptions['currencyDisplay'];
  title: ReactElement;
  subtitle?: ReactElement;
  className?: string;
  size?: Size;
  align?: TypographyProps['align'];
};

const amountVariants: Record<Size, TypographyProps['variant']> = {
  big: 'h1',
  medium: 'h2',
  small: 'h3',
  compact: 'h2',
};

const labelVariants: Record<Size, TypographyProps['variant']> = {
  big: 'b3',
  medium: 'b4',
  small: 'b4',
  compact: 'b2',
};

const labelFontWeightVariants: Record<Size, TypographyProps['fontWeight']> = {
  big: 'medium',
  medium: 'medium',
  small: 'medium',
  compact: 'heavy',
};

export const Amount = ({
  amount,
  currency,
  currencyDisplay,
  title,
  subtitle,
  size = 'big',
  className,
  align,
}: Props) => {
  const theme = useTheme();

  return (
    <div className={className} css={styles.root} data-testid="amount">
      <Typography
        align={align}
        color="textHighlight"
        css={styles.label(theme)}
        fontWeight={labelFontWeightVariants[size]}
        textTransform="uppercase"
        variant={labelVariants[size]}>
        {title}
      </Typography>
      <Typography
        align={align}
        component="div"
        css={styles.amount(theme)}
        data-testid="amount"
        fontWeight="heavy"
        variant={amountVariants[size]}>
        <FormattedCurrencyNumber
          currency={currency}
          currencyDisplay={currencyDisplay}
          value={amount}
        />
      </Typography>
      {!isNil(subtitle) && (
        <Typography color="textTertiary" component="div" variant="b2">
          {subtitle}
        </Typography>
      )}
    </div>
  );
};
